import get from "lodash/get";
import React from "react";
import { Permission } from "@ticketingplatform/api/dist/__generated__/globalTypes";
import { ItemType } from "antd/es/menu/hooks/useItems";
import { ROUTE_TO_PERMISSIONS_MAP } from "const/paths";
import { hasPermissions } from "hooks/useHasPermissions/utils";
import { isKeyOfObject } from "utils/types";
import { Link } from "react-router-dom";

export const filterMenuItemsWithAuth = <TItem extends ItemType = ItemType>(
  menuItems: TItem[],
  userPermissions: Permission[]
): TItem[] => {
  const filtered: TItem[] = [];

  const predicate = (item: NonNullable<TItem>): boolean => {
    if (!("key" in item)) {
      return true;
    }

    const { key } = item;
    const keys = get(item, "nestedKeys") as string[] | undefined;
    const allKeys = [...(key ? [key] : []), ...(keys || [])];

    if (!allKeys.length) {
      return true;
    }

    const permissionKey = allKeys.find((keyToCheck) => {
      if (!isKeyOfObject(keyToCheck, ROUTE_TO_PERMISSIONS_MAP)) {
        return false;
      }

      const permissions = ROUTE_TO_PERMISSIONS_MAP[keyToCheck];

      return hasPermissions(userPermissions, permissions);
    });

    if (
      permissionKey &&
      "label" in item &&
      item.label &&
      React.isValidElement(item.label) &&
      item.label.type === Link
    ) {
      const { label } = item;

      // eslint-disable-next-line no-param-reassign
      item.label = React.cloneElement(label, {
        ...label.props,
        to: permissionKey,
      });
    }

    return !!permissionKey;
  };

  menuItems.forEach((item) => {
    if (!item) {
      return;
    }

    if ("children" in item && item.children && Array.isArray(item.children)) {
      const filteredChildren = filterMenuItemsWithAuth(
        item.children,
        userPermissions
      );

      if (filteredChildren.length > 0) {
        filtered.push({
          ...(item as TItem),
          children: filteredChildren,
        });
      }

      return;
    }

    if (predicate(item)) {
      filtered.push(item);
    }
  });

  return filtered;
};
